function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

import parseISO from "date-fns/parseISO";
import baseToDate from "date-fns/toDate";
import * as React from "react";
/**
 * Extracts props from an object and returns the remainingg props as another
 * object. The difference between using this and object rest spread is this
 * will not copy over properties that are `undefined`.
 */

export function extractProps(obj, keys) {
  var withKeys = {};
  var withoutKeys = {};
  Object.keys(obj).forEach(function (key) {
    var value = obj[key];

    if (value !== undefined) {
      if (keys.indexOf(key) === -1) {
        withoutKeys[key] = value;
      } else {
        withKeys[key] = value;
      }
    }
  });
  return [withKeys, withoutKeys];
}
export function extractStyledSystemProps(obj, styledConfigs) {
  if (!Array.isArray(styledConfigs)) {
    // eslint-disable-next-line no-param-reassign
    styledConfigs = [styledConfigs];
  }

  var keys = styledConfigs.reduce(function (keysMemo, styledConfig) {
    return [].concat(keysMemo, Object.keys(styledConfig.config || styledConfig));
  }, []);
  return extractProps(obj, keys);
}

function updateRef(ref, instance) {
  if (!ref) return;

  if (typeof ref === "function") {
    ref(instance);
    return;
  }

  ref.current = instance;
}
/**
 * Compose multiple ref functions into a single ref function.
 *
 *     <Button ref={composeRefs([getTriggerRef, getResizerRef])} />
 */


export function composeRefs(refs) {
  return function (instance) {
    refs.forEach(function (ref) {
      return updateRef(ref, instance);
    });
  };
}
/**
 * Shortcut for forwarded a ref to the `innerRef` prop.
 */

export function forwardInnerRef(SourceComponent) {
  var Component = React.forwardRef(function (props, ref) {
    return React.createElement(SourceComponent, _extends({
      innerRef: ref
    }, props));
  });
  Component.displayName = SourceComponent.displayName || SourceComponent.name;
  return Component;
}
/* Date Utils */

export function supportsTimeZoneFormat() {
  try {
    new Intl.DateTimeFormat("en-US", {
      timeZone: "Australia/Sydney",
      timeZoneName: "long"
    }).format();
  } catch (error) {
    return false;
  }

  return true;
}
export function getTimeZoneFormat(timeZone) {
  if (timeZone == null || !supportsTimeZoneFormat()) {
    return undefined;
  }

  return timeZone;
}
export function toDate(value, defaultValue) {
  var date;

  try {
    if (typeof value === "string") {
      date = parseISO(value);
    } else {
      date = baseToDate(value);
    }
  } catch (error) {
    if (defaultValue) return defaultValue;
    throw new Error("Could not convert " + value + " to date");
  }

  if (!date || Number.isNaN(date.getTime())) {
    if (defaultValue) return defaultValue;
    throw new Error("Could not convert " + value + " to date");
  }

  return date;
}
export function parseDate(value) {
  if (typeof value === "string") {
    // We could use date-fns/parse, but that adds a signicant amount to our JS
    // bundle (~7 KB gzipped). If it's added elsewhere for a more complex reason, this could
    // be simplified by switching.
    var match = value.match(/([01]?\d)[-/]([0123]?\d)[-/](\d{4})/);

    if (match) {
      var year = parseInt(match[3], 10);
      var month = parseInt(match[1], 10) - 1;
      var day = parseInt(match[2], 10);
      return new Date(year, month, day);
    }
  }

  return toDate(value);
}
export function toISODateString(value) {
  var date = parseDate(value);

  if (date == null) {
    return null;
  } // We could use date-fns/format, but that adds a signicant amount to our JS
  // bundle (~3.3 KB gzipped). If it's added elsewhere for a more complex reason, this could
  // be simplified by switching.


  var year = date.getFullYear();
  var month = (date.getMonth() + 1).toString().padStart(2, "0");
  var dateString = date.getDate().toString().padStart(2, "0");
  return year + "-" + month + "-" + dateString;
}